import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ViewChild,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { keyBy as _keyBy } from 'lodash';
import moment from 'moment';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { DxButtonComponent } from 'devextreme-angular/ui/button';
import { DxButtonGroupComponent } from 'devextreme-angular/ui/button-group';
import { DxCheckBoxComponent } from 'devextreme-angular/ui/check-box';
import {
    DxDataGridComponent,
    DxDataGridTypes,
} from 'devextreme-angular/ui/data-grid';
import { DxPopupComponent } from 'devextreme-angular/ui/popup';
import { DxTextBoxComponent } from 'devextreme-angular/ui/text-box';
import { DxTemplateDirective } from 'devextreme-angular/core';
import {
    DxiColumnComponent,
    DxoEditingComponent,
    DxoFilterRowComponent,
    DxoPagerComponent,
    DxoPagingComponent,
    DxoSearchPanelComponent,
} from 'devextreme-angular/ui/nested';
import {
    AbcAnalysisResponse,
    AbcAnalysisService,
} from 'src/app/shared/services/abc-analysis.service';
import { ORDERABLE_PRODUCTS_QUERY } from 'src/app/shared/services/reordering.service';
import {
    MessageType,
    NotificationService,
    PurchaseOrderService,
    UnifiedQueryV2Service,
    UtilService,
    adminProductUrl,
} from 'src/app/shared/services';
import { FormatNumberPipe } from '../../../shared/pipes/format-number.pipe';
import {
    EMPTY_SUPPLIER_INDEX,
    Supplier1688Index,
    Supplier1688Item,
    SupplierOverridesDoc,
    SupplierShop,
    SupplierStatus,
    buildSupplierIndex,
    supplierStatus,
} from '../supplier-1688';
import { Supplier1688Service } from '../supplier-1688.service';
import { Supplier1688EditorComponent } from '../supplier-1688-editor/supplier-1688-editor.component';
import { Supplier1688LinksComponent } from '../supplier-1688-links/supplier-1688-links.component';

interface InventoryProduct {
    _id: number;
    sku: string;
    title: string;
    remain: number;
    stock_status: string;
}

type StatusFilter = 'check' | 'missing' | 'decided' | 'all';

interface PageRow extends InventoryProduct {
    class: string | null;
    total_sale: number | null;
    // Paused for ordering or discontinued: no order coming to fix it for.
    idle: boolean;
    status: SupplierStatus;
    statusText: string;
    note: string | null;
    supplier: Supplier1688Item | null;
}

const STATUS_TEXT: Record<SupplierStatus, string> = {
    check: 'ต้องตรวจ',
    matched: 'match แล้ว',
    confirmed: 'ยืนยันแล้ว',
    user: 'ตั้งเอง',
    'not-on-1688': 'ไม่ได้ซื้อจาก 1688',
    missing: 'ไม่มีข้อมูล',
};

const FILTERS: Record<
    StatusFilter,
    { label: string; statuses: SupplierStatus[] | null }
> = {
    check: { label: 'ต้องตรวจ', statuses: ['check'] },
    missing: { label: 'ไม่มีข้อมูล', statuses: ['missing'] },
    // Everything a person has decided, which no sync will change.
    decided: {
        label: 'ตั้งเอง / ยืนยันแล้ว',
        statuses: ['user', 'confirmed', 'not-on-1688'],
    },
    all: { label: 'ทั้งหมด', statuses: null },
};

// Where wrong 1688 matches get fixed and missing links get filled in, biggest
// sellers first. What is saved here sits apart from the synced rows, so no
// later sync can undo it.
@Component({
    selector: 'app-supplier-1688-page',
    templateUrl: './supplier-1688-page.component.html',
    styleUrls: ['./supplier-1688-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DxButtonComponent,
        DxButtonGroupComponent,
        DxCheckBoxComponent,
        DxDataGridComponent,
        DxPopupComponent,
        DxTextBoxComponent,
        DxTemplateDirective,
        DxiColumnComponent,
        DxoEditingComponent,
        DxoFilterRowComponent,
        DxoPagerComponent,
        DxoPagingComponent,
        DxoSearchPanelComponent,
        FormatNumberPipe,
        Supplier1688EditorComponent,
        Supplier1688LinksComponent,
    ],
})
export class Supplier1688PageComponent implements AfterViewInit {
    private cdref = inject(ChangeDetectorRef);
    private destroyRef = inject(DestroyRef);
    private router = inject(Router);
    private notificationService = inject(NotificationService);
    private utilService = inject(UtilService);
    private unifiedQueryV2Service = inject(UnifiedQueryV2Service);
    private abcAnalysisService = inject(AbcAnalysisService);
    private purchaseOrderService = inject(PurchaseOrderService);
    private supplierService = inject(Supplier1688Service);

    @ViewChild('dataGrid', { static: true })
    private dataGrid!: DxDataGridComponent;

    @ViewChild('editor', { static: true })
    private editor!: Supplier1688EditorComponent;

    readonly adminProductUrl = adminProductUrl;
    loading = false;
    searchText = '';
    hideIdle = true;
    filterItems: { key: StatusFilter; text: string }[] = [];
    selectedFilterKeys: StatusFilter[] = ['check'];
    visibleRows: PageRow[] = [];
    shopsPopupVisible = false;
    index: Supplier1688Index = EMPTY_SUPPLIER_INDEX;

    private rows: PageRow[] = [];
    private products: InventoryProduct[] = [];
    private abcByPid: Record<number, AbcAnalysisResponse> = {};
    private idlePids = new Set<number>();

    ngAfterViewInit(): void {
        this.load();
        this.cdref.detectChanges();
    }

    load(): void {
        if (this.loading) {
            return;
        }
        this.loading = true;
        this.dataGrid.instance.beginCustomLoading('');
        forkJoin([
            this.unifiedQueryV2Service.query<InventoryProduct>({
                db: 'lnw_crawler',
                col: 'inventory',
                query: ORDERABLE_PRODUCTS_QUERY,
                options: {
                    projection: {
                        sku: 1,
                        title: 1,
                        remain: 1,
                        stock_status: 1,
                    },
                },
            }),
            this.supplierService.getRows(),
            this.supplierService.getOverrides(),
            // Only order the list and trim it, so losing them costs little.
            this.abcAnalysisService
                .get(moment().subtract(1, 'year'))
                .pipe(catchError(() => of([] as AbcAnalysisResponse[]))),
            this.purchaseOrderService
                .getDoc()
                .pipe(
                    catchError(() =>
                        of({ orders: [], paused_temp: [], paused_long: [] }),
                    ),
                ),
        ])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: ([products, supplierRows, overrides, abc, doc]) => {
                    this.products = products;
                    this.index = buildSupplierIndex(supplierRows, overrides);
                    this.abcByPid = _keyBy(abc, 'pid');
                    this.idlePids = new Set(
                        [...doc.paused_temp, ...doc.paused_long].map(
                            (entry) => entry.pid,
                        ),
                    );
                    this.rebuildRows();
                    this.finishLoading();
                },
                error: (err: unknown) => {
                    this.finishLoading();
                    this.notifyError(err);
                },
            });
    }

    private finishLoading(): void {
        this.loading = false;
        this.dataGrid.instance.endCustomLoading();
    }

    private rebuildRows(): void {
        this.rows = this.products.map((product) => {
            const item = this.index.byPid[product._id];
            const override = this.index.overrides.products[product._id];
            const status = supplierStatus(item, override);
            const abc = this.abcByPid[product._id];
            return {
                ...product,
                class: abc?.class ?? null,
                total_sale: abc?.total_sale ?? null,
                idle:
                    this.idlePids.has(product._id) ||
                    product.stock_status === 'discontinued',
                status,
                statusText: STATUS_TEXT[status],
                note: override?.note ?? item?.reviewNote ?? null,
                supplier: item ?? null,
            };
        });
        this.applyFilter();
    }

    applyFilter(): void {
        const candidates = this.hideIdle
            ? this.rows.filter((row) => !row.idle)
            : this.rows;
        const rowsFor = (key: StatusFilter): PageRow[] => {
            const { statuses } = FILTERS[key];
            return statuses
                ? candidates.filter((row) => statuses.includes(row.status))
                : candidates;
        };
        this.filterItems = (Object.keys(FILTERS) as StatusFilter[]).map(
            (key) => ({
                key,
                text: `${FILTERS[key].label} (${rowsFor(key).length})`,
            }),
        );
        this.visibleRows = rowsFor(this.selectedFilterKeys[0]);
    }

    onFilterPicked(e: { itemData?: { key: StatusFilter } }): void {
        if (e.itemData) {
            this.selectedFilterKeys = [e.itemData.key];
            this.applyFilter();
        }
    }

    backToReordering(): void {
        this.router.navigate(['/secure/utilities/reordering']);
    }

    edit(row: PageRow): void {
        this.editor.open(
            { pid: row._id, sku: row.sku, title: row.title },
            this.index,
        );
    }

    // The common case for a row marked to check: it was right all along.
    confirm(row: PageRow): void {
        if (!row.supplier) {
            return;
        }
        this.supplierService
            .saveProduct(row._id, {
                confirmedOffers: row.supplier.parts.map((part) => part.offerId),
                note: this.index.overrides.products[row._id]?.note ?? null,
            })
            .subscribe({
                next: (doc) => {
                    this.onOverridesSaved(doc);
                    this.notificationService.pushMessage({
                        displayTime: 3000,
                        message: `ยืนยัน ${row.sku} แล้ว`,
                        messageType: MessageType.success,
                    });
                },
                error: (err: unknown) => this.notifyError(err),
            });
    }

    onOverridesSaved(doc: SupplierOverridesDoc): void {
        this.index = buildSupplierIndex(this.index.rows, doc);
        this.rebuildRows();
    }

    openShops(): void {
        this.shopsPopupVisible = true;
    }

    // A name edit landed. The grid holds its edit until this answers, and drops
    // it if the save failed, so it never shows a name that wasn't stored. The
    // list is rebuilt from the saved document, so an empty name shows at once
    // what the shop falls back to.
    onShopUpdating(
        e: DxDataGridTypes.RowUpdatingEvent<SupplierShop, string>,
    ): void {
        const name = (e.newData.name ?? '').trim();
        e.cancel = new Promise<boolean>((resolve) => {
            this.supplierService.saveShopName(e.oldData.url, name).subscribe({
                next: (doc) => {
                    resolve(false);
                    this.onOverridesSaved(doc);
                },
                error: (err: unknown) => {
                    resolve(true);
                    this.notifyError(err);
                },
            });
        });
    }

    private notifyError(err: unknown): void {
        console.error(err);
        this.notificationService.pushMessage({
            displayTime: 6000,
            message: this.utilService.errorToString(err),
            messageType: MessageType.error,
        });
    }
}
